import { GetTagBadgeColor } from '../constants/columns';
import { TBadge } from './primitives';
import { useTokens } from './tokens';

// Alias pra reduzir diff. Toda referência local usa lowercase histórico.
const getTagBadgeColor = GetTagBadgeColor;

/**
 * Picker de marca/tag compartilhado entre Toolbar (filtro temporário) e
 * PresetBuilder (filtro fixo do preset). Mesmo visual nos 2 lugares:
 *
 *   - Header com label + botão "Limpar" (se há seleção)
 *   - Switch "Exigir TODAS as tags" (só pra kind='tag')
 *   - Lista de checkboxes; tags renderizam como TBadge colorida
 *
 * Props:
 *   kind        — 'brand' | 'tag'
 *   options     — string[] disponíveis pra escolher
 *   selected    — string[] atualmente selecionados
 *   matchAll    — bool (só pra kind='tag', sinal do AND)
 *   onToggle    — (value: string) => void; chamado quando user clica num item
 *   onClear     — () => void; limpa toda a seleção
 *   onMatchAllChange — (next: bool|undefined) => void; só pra kind='tag'
 */
export function BrandTagPicker({
    kind,
    options = [],
    selected = [],
    matchAll = false,
    onToggle,
    onClear,
    onMatchAllChange,
}) {
    const T = useTokens();
    const isBrand = kind === 'brand';

    return (
        <div style={{ padding: 4, minWidth: 220, maxHeight: 360, overflowY: 'auto' }}>
            <div
                style={{
                    padding: '6px 10px',
                    fontSize: 11,
                    color: T.muted,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                }}
            >
                <span style={{ flex: 1 }}>{isBrand ? 'Marcas' : 'Tags'}</span>
                {selected.length > 0 && onClear && (
                    <button
                        type="button"
                        onClick={onClear}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: T.accent,
                            fontSize: 10,
                            cursor: 'pointer',
                            padding: 0,
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.4,
                        }}
                    >
                        Limpar
                    </button>
                )}
            </div>
            {!isBrand && onMatchAllChange && (
                <label
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 12px',
                        fontSize: 12,
                        cursor: 'pointer',
                        background: matchAll ? T.bgSoft : 'transparent',
                        color: T.ink,
                        borderBottom: `1px solid ${T.line}`,
                    }}
                    title="Se ativado, o produto precisa ter TODAS as tags selecionadas. Sem isso, basta UMA."
                >
                    <input
                        type="checkbox"
                        checked={matchAll}
                        onChange={() => onMatchAllChange(matchAll ? undefined : true)}
                        style={{ cursor: 'pointer' }}
                    />
                    <span>Exigir TODAS as tags</span>
                </label>
            )}
            {options.length === 0 && (
                <div style={{ padding: '8px 14px', fontSize: 12, color: T.muted }}>
                    {isBrand ? 'Sem marcas' : 'Sem tags'}
                </div>
            )}
            {options.map((v) => {
                const checked = selected.includes(v);
                return (
                    <label
                        key={v}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 12px',
                            fontSize: 12,
                            cursor: 'pointer',
                            background: checked ? T.bgSoft : 'transparent',
                            color: T.ink,
                        }}
                    >
                        <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggle?.(v)}
                            style={{ cursor: 'pointer' }}
                        />
                        {isBrand ? (
                            <span style={{ textTransform: 'uppercase' }}>{v}</span>
                        ) : (
                            <TBadge color={getTagBadgeColor(v)}>{v}</TBadge>
                        )}
                    </label>
                );
            })}
        </div>
    );
}
